import { store } from '@/store';

import { orgExtrasBase } from '@/services/emrAccessUsers';

import { ClinicLetters, lettersFromRecord, LetterType } from './letterTemplates';

export interface LetterEditorSession {
  id: string;
  url: string;
  postgridTemplateId: string;
}

export interface LetterVolume {
  mtd: number;
  lastMonth: number;
  rangeCount: number;
  truncated: boolean;
  unitCostUsd: number;
}

function authHeaders(json = false): HeadersInit {
  const headers: Record<string, string> = {};
  const token = store.getState().auth.token;
  if (token) headers.Authorization = `Bearer ${token}`;
  if (json) headers['Content-Type'] = 'application/json';
  return headers;
}

async function extrasFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const extras = orgExtrasBase();
  if (!extras) throw new Error('Letter templates are not available in this environment.');
  const response = await fetch(`${extras}${path}`, {
    ...init,
    headers: { ...authHeaders(Boolean(init?.body)), ...(init?.headers ?? {}) }
  });
  const text = await response.text();
  const payload = text ? (JSON.parse(text) as { message?: string }) : {};
  if (!response.ok) {
    const error = new Error(
      payload.message || `Letter request failed (${response.status})`
    ) as Error & {
      status?: number;
    };
    error.status = response.status;
    throw error;
  }
  return payload as T;
}

export async function loadClinicLetters(
  organizationId: string
): Promise<{ letters: ClinicLetters; raw: Record<string, unknown> }> {
  try {
    const payload = await extrasFetch<{ record?: { data?: Record<string, unknown> } }>(
      `/organizations/${organizationId}/records/letters`
    );
    const raw =
      payload.record?.data && typeof payload.record.data === 'object' ? payload.record.data : {};
    return { letters: lettersFromRecord(payload), raw };
  } catch (error) {
    if ((error as { status?: number }).status === 404) {
      return { letters: { lettersEnabled: false, templates: [] }, raw: {} };
    }
    throw error;
  }
}

export async function saveClinicLetters(
  organizationId: string,
  raw: Record<string, unknown>,
  letters: ClinicLetters
): Promise<Record<string, unknown>> {
  const existing = Array.isArray(raw.templates) ? raw.templates : [];
  const templates = existing.map((row) => {
    if (!row || typeof row !== 'object') return row;
    const source = row as Record<string, unknown>;
    const next = letters.templates.find((item) => item.letterType === source.letterType);
    if (!next) return row;
    return {
      ...source,
      postgridTemplateId: next.postgridTemplateId,
      enabled: next.enabled,
      deviceScope: next.deviceScope,
      frequencyMode: next.frequencyMode,
      cadenceDays: next.cadenceDays
    };
  });
  const data = { ...raw, lettersEnabled: letters.lettersEnabled, templates };
  await extrasFetch(`/organizations/${organizationId}/records/letters`, {
    method: 'PUT',
    body: JSON.stringify({ data })
  });
  return data;
}

export async function openLetterEditor(
  organizationId: string,
  letterType: LetterType,
  templateId: string,
  createCopy: boolean
): Promise<LetterEditorSession> {
  const session = await extrasFetch<LetterEditorSession>(
    `/organizations/${organizationId}/letters/${letterType}/editor-session`,
    {
      method: 'POST',
      body: JSON.stringify({ templateId, createCopy })
    }
  );
  if (!session?.url) throw new Error('The template editor did not open.');
  return session;
}

export async function loadLetterVolume(
  organizationId: string,
  templateIds: string[],
  from: string,
  to: string
): Promise<LetterVolume> {
  const params = new URLSearchParams();
  if (templateIds.length) params.set('templates', templateIds.join(','));
  if (from) params.set('from', from);
  if (to) params.set('to', to);
  return extrasFetch<LetterVolume>(
    `/organizations/${organizationId}/letters/metrics?${params.toString()}`
  );
}
